import { useCallback, useState } from "react"
import { useQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetSystemDataConnectionsDocument,
  GetAvailableConnectionsDocument,
  CreateDataConnectionDocument,
  UpdateDataConnectionStatusDocument,
  RemoveDataConnectionDocument,
  type GetSystemDataConnectionsQuery,
} from "@hydrologiq/gql-experience/graphql"
import { useSystemRefetch } from "../providers/helpers/useSystem"
import { useSectionEvent, emitSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"

/**
 * Derived from the generated GraphQL query type so it stays in sync with the
 * schema (including fields like `assetOwned`) rather than drifting from a
 * hand-written duplicate. Scoped to exactly the fields this query selects.
 */
export type DataConnection = GetSystemDataConnectionsQuery["getSystemDataConnections"][number]

export interface AvailableConnection {
  monitorId: string
  monitorName: string | null
  connectorType: string
}

export interface UnlinkedInstance {
  monitoringInstanceId: string
  externalId: string | null
  name: string | null
}

export interface CreateDataConnectionInput {
  systemId: string
  monitorId: string
  externalId?: string | null
  monitoringInstanceId?: string | null
  deviceModel?: string | null
}

export interface UpdateDataConnectionStatusInput {
  monitoringInstanceId: string
  active: boolean
  volume?: number | null
  minPressure?: number | null
}

export interface UseDataConnectionsReturn {
  connections: DataConnection[]
  loading: boolean
  error: string | undefined
  availableConnections: AvailableConnection[]
  loadingAvailable: boolean
  createConnection: (input: CreateDataConnectionInput) => Promise<void>
  updateStatus: (input: UpdateDataConnectionStatusInput) => Promise<void>
  removeConnection: (monitoringInstanceId: string) => Promise<void>
  refetchConnections: () => void
  creating: boolean
  updating: boolean
  removing: boolean
}

export function useDataConnections(systemId: string): UseDataConnectionsReturn {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [removing, setRemoving] = useState(false)
  const systemRefetch = useSystemRefetch()

  const { data, loading, error, refetch } = useQueryWithEndpoint(
    GetSystemDataConnectionsDocument,
    {
      variables: { systemId },
      skip: !systemId,
    },
    "primary",
  )

  const { data: availableData, loading: loadingAvailable, refetch: refetchAvailable } = useQueryWithEndpoint(
    GetAvailableConnectionsDocument,
    {
      variables: {},
      skip: !systemId,
    },
    "primary",
  )

  // Listen for connector changes from other sections (e.g. hub creating/deleting connectors)
  const handleConnectorChange = useCallback(() => {
    refetchAvailable()
  }, [refetchAvailable])
  useSectionEvent(SECTION_EVENTS.CONNECTOR_CREATED, handleConnectorChange)
  useSectionEvent(SECTION_EVENTS.CONNECTOR_DELETED, handleConnectorChange)

  // Listen for asset changes from other sections (e.g. assets creating/unassigning)
  const handleAssetChange = useCallback(() => {
    systemRefetch?.()
  }, [systemRefetch])
  useSectionEvent(SECTION_EVENTS.ASSET_CREATED, handleAssetChange)
  useSectionEvent(SECTION_EVENTS.ASSET_DELETED, handleAssetChange)
  useSectionEvent(SECTION_EVENTS.ASSET_ASSIGNED, handleAssetChange)
  useSectionEvent(SECTION_EVENTS.ASSET_UNASSIGNED, handleAssetChange)

  // Listen for monitoring status changes (e.g. group toggle mutations)
  const handleMonitoringChange = useCallback(() => {
    refetch()
    systemRefetch?.()
  }, [refetch, systemRefetch])
  useSectionEvent(SECTION_EVENTS.MONITORING_STATUS_CHANGED, handleMonitoringChange)

  // Listen for asset mapping changes so connections re-group immediately
  const handleAssetMapped = useCallback(() => {
    refetch()
  }, [refetch])
  useSectionEvent(SECTION_EVENTS.ASSET_MAPPED, handleAssetMapped)

  const refetchConnections = useCallback(() => {
    refetch()
    systemRefetch?.()
  }, [refetch, systemRefetch])

  const [createDataConnectionMutation] = useMutationWithEndpoint(
    CreateDataConnectionDocument,
    undefined,
    "primary",
  )

  const [updateDataConnectionStatusMutation] = useMutationWithEndpoint(
    UpdateDataConnectionStatusDocument,
    undefined,
    "primary",
  )

  const [removeDataConnectionMutation] = useMutationWithEndpoint(
    RemoveDataConnectionDocument,
    undefined,
    "primary",
  )

  const createConnection = useCallback(
    async (input: CreateDataConnectionInput) => {
      setCreating(true)
      try {
        const result = await createDataConnectionMutation({
          variables: { input },
        })
        const { data, errors } = result
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
        if (!data?.createDataConnection) {
          throw new Error("Failed to create data connection")
        }
        await refetch()
        await refetchAvailable()
        systemRefetch?.()
      } catch (err: any) {
        // Extract meaningful error message from GraphQL or network errors
        const message =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to create data connection"
        throw new Error(message)
      } finally {
        setCreating(false)
      }
    },
    [createDataConnectionMutation, refetch, refetchAvailable, systemRefetch],
  )

  const updateStatus = useCallback(
    async (input: UpdateDataConnectionStatusInput) => {
      setUpdating(true)
      try {
        const result = await updateDataConnectionStatusMutation({
          variables: { input },
        })
        const { data, errors } = result
        if (errors?.length) {
          const error = errors[0] as any
          const wrappedError: any = new Error(error.message)
          wrappedError.graphQLError = error
          throw wrappedError
        }
        if (!data?.updateDataConnectionStatus) {
          throw new Error("Failed to update connection status")
        }
        await refetch()
        systemRefetch?.()
        emitSectionEvent(SECTION_EVENTS.MONITORING_STATUS_CHANGED)      } catch (err: any) {
        await refetch()
        const message =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to update connection status"
        throw new Error(message)
      } finally {
        setUpdating(false)
      }
    },
    [updateDataConnectionStatusMutation, refetch, systemRefetch],
  )

  const connections: DataConnection[] = data?.getSystemDataConnections ?? []
  const availableConnections = (availableData?.getAvailableConnections ?? []) as AvailableConnection[]

  const removeConnection = useCallback(
    async (monitoringInstanceId: string) => {
      setRemoving(true)
      try {
        const { errors } = await removeDataConnectionMutation({
          variables: { monitoringInstanceId },
        })
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
        await refetch()
        systemRefetch?.()
      } catch (err: any) {
        await refetch()
        throw err
      } finally {
        setRemoving(false)
      }
    },
    [removeDataConnectionMutation, refetch, systemRefetch],
  )

  return {
    connections,
    loading,
    error: error?.message,
    availableConnections,
    loadingAvailable,
    createConnection,
    updateStatus,
    removeConnection,
    refetchConnections,
    creating,
    updating,
    removing,
  }
}
